import simpleRestProvider from 'ra-data-simple-rest';
import { fetchUtils, DataProvider } from 'react-admin';

const httpClient = (url: string, options: any = {}) => {
  const token = localStorage.getItem('token');
  
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }
  
  if (token) {
    options.headers.set('Authorization', `Bearer ${token}`);
  }
  
  return fetchUtils.fetchJson(url, options);
};

const apiUrl = process.env.REACT_APP_API_URL || 'https://api.sushismile154.ru';
const restProvider = simpleRestProvider(apiUrl, httpClient);

export const dataProvider: DataProvider = {
  ...restProvider,
  
  // Переопределяем методы для соответствия нашему API
  getList: (resource, params) => {
    const page = params.pagination?.page ?? 1;
    const perPage = params.pagination?.perPage ?? 10;
    const field = params.sort?.field ?? 'id';
    const order = params.sort?.order ?? 'ASC';
    const query = {
      sort: JSON.stringify([field, order]),
      range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
      filter: JSON.stringify(params.filter),
    };
    const url = `${apiUrl}/${resource}?${new URLSearchParams(query)}`;

    return httpClient(url).then(({ headers, json }) => ({
      data: json,
      total: parseInt(headers.get('content-range')?.split('/')[1] || json.length, 10),
    }));
  },
  
  getOne: (resource, params) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`).then(({ json }) => ({
      data: json,
    })),
    
  getMany: (resource, params) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    const url = `${apiUrl}/${resource}?${new URLSearchParams(query)}`;
    return httpClient(url).then(({ json }) => ({ data: json }));
  },
  
  create: async (resource, params) => {
    let data = { ...params.data } as any;
    
    // Handle delivery zones coordinate validation
    if (resource === 'delivery-zones') {
      console.log('Creating delivery zone with data:', data);
      
      if (data.centerLat !== undefined) {
        data.centerLat = parseFloat(data.centerLat);
        if (isNaN(data.centerLat) || data.centerLat < -90 || data.centerLat > 90) {
          throw new Error('centerLat must be a valid number between -90 and 90');
        }
      }
      if (data.centerLng !== undefined) {
        data.centerLng = parseFloat(data.centerLng);
        if (isNaN(data.centerLng) || data.centerLng < -180 || data.centerLng > 180) {
          throw new Error('centerLng must be a valid number between -180 and 180');
        }
      }
      if (data.radiusMeters !== undefined) {
        data.radiusMeters = parseInt(data.radiusMeters);
        if (isNaN(data.radiusMeters) || data.radiusMeters < 100) {
          throw new Error('radiusMeters must be a valid number greater than 100');
        }
      }
      if (data.deliveryPrice !== undefined) {
        data.deliveryPrice = parseFloat(data.deliveryPrice);
        if (isNaN(data.deliveryPrice) || data.deliveryPrice < 0) {
          throw new Error('deliveryPrice must be a valid number greater than or equal to 0');
        }
      }
      
      console.log('Processed delivery zone data:', data);
    }
    
    if ((resource === 'products' || resource === 'products/admin') && Array.isArray((params as any).data?.newPhotos)) {
      const files = (params as any).data.newPhotos as Array<{ rawFile: File }>;
      const form = new FormData();
      files.forEach((f) => form.append('files', f.rawFile));
      const res = await fetch(`${apiUrl}/uploads/batch`, { method: 'POST', body: form });
      const json = await res.json();
      const uploadedUrls: string[] = json.urls || [];
      data.photos = [...(data.photos || []), ...uploadedUrls];
      delete data.newPhotos;
    }
    // Normalize arrays to strings only
    if (Array.isArray(data.photos)) {
      data.photos = data.photos
        .map((v: any) => (typeof v === 'string' ? v : v?.src || v?.url))
        .filter((v: any) => typeof v === 'string' && v.length > 0);
    }
    if (Array.isArray(data.composition)) {
      data.composition = data.composition
        .map((v: any) => (typeof v === 'string' ? v : ''))
        .filter((v: any) => v.length > 0);
    }
    if (Array.isArray(data.tags)) {
      data.tags = data.tags
        .map((v: any) => (typeof v === 'string' ? v : ''))
        .filter((v: any) => v.length > 0);
    }
    
    return httpClient(`${apiUrl}/${resource}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }).then(({ json }) => ({ data: json }));
  },
    
  update: async (resource, params) => {
    let data = { ...params.data } as any;
    
    // Handle delivery zones coordinate validation
    if (resource === 'delivery-zones') {
      console.log('Updating delivery zone with data:', data);
      
      if (data.centerLat !== undefined) {
        data.centerLat = parseFloat(data.centerLat);
        if (isNaN(data.centerLat) || data.centerLat < -90 || data.centerLat > 90) {
          throw new Error('centerLat must be a valid number between -90 and 90');
        }
      }
      if (data.centerLng !== undefined) {
        data.centerLng = parseFloat(data.centerLng);
        if (isNaN(data.centerLng) || data.centerLng < -180 || data.centerLng > 180) {
          throw new Error('centerLng must be a valid number between -180 and 180');
        }
      }
      if (data.radiusMeters !== undefined) {
        data.radiusMeters = parseInt(data.radiusMeters);
        if (isNaN(data.radiusMeters) || data.radiusMeters < 100) {
          throw new Error('radiusMeters must be a valid number greater than 100');
        }
      }
      if (data.deliveryPrice !== undefined) {
        data.deliveryPrice = parseFloat(data.deliveryPrice);
        if (isNaN(data.deliveryPrice) || data.deliveryPrice < 0) {
          throw new Error('deliveryPrice must be a valid number greater than or equal to 0');
        }
      }
      
      console.log('Processed delivery zone data for update:', data);
    }
    
    if ((resource === 'products' || resource === 'products/admin') && Array.isArray((params as any).data?.newPhotos)) {
      const files = (params as any).data.newPhotos as Array<{ rawFile: File }>;
      const form = new FormData();
      files.forEach((f) => form.append('files', f.rawFile));
      const res = await fetch(`${apiUrl}/uploads/batch`, { method: 'POST', body: form });
      const json = await res.json();
      const uploadedUrls: string[] = json.urls || [];
      data.photos = [...(data.photos || []), ...uploadedUrls];
      delete data.newPhotos;
    }
    // Normalize arrays to strings only
    if (Array.isArray(data.photos)) {
      data.photos = data.photos
        .map((v: any) => (typeof v === 'string' ? v : v?.src || v?.url))
        .filter((v: any) => typeof v === 'string' && v.length > 0);
    }
    if (Array.isArray(data.composition)) {
      data.composition = data.composition
        .map((v: any) => (typeof v === 'string' ? v : ''))
        .filter((v: any) => v.length > 0);
    }
    if (Array.isArray(data.tags)) {
      data.tags = data.tags
        .map((v: any) => (typeof v === 'string' ? v : ''))
        .filter((v: any) => v.length > 0);
    }
    
    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }).then(({ json }) => ({ data: json }));
  },
    
  delete: (resource, params) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'DELETE',
    }).then(({ json }) => {
      console.log('Delete response:', json);
      const fallback = { id: (params as any).id } as any;
      const data = ((params as any).previousData ?? fallback) as any;
      return { data } as any;
    }).catch((error) => {
      console.error('Delete error:', error);
      throw error;
    }) as any,

  deleteMany: (resource, params) => {
    return Promise.all(
      params.ids.map((id) =>
        httpClient(`${apiUrl}/${resource}/${id}`, {
          method: 'DELETE',
        })
      )
    ).then(() => ({ data: params.ids }));
  },
};